/**
 * Map Bundle related TypeScript type definitions.
 *
 * Each event can have multiple map bundles, each with its own AoO bounds
 * and optional faction scope.
 */

export type MapBundleStatusType = 'queued' | 'generating' | 'ready' | 'error';

/**
 * MapTiler style IDs the cartograph worker can render with. Keep in sync
 * with the backend `MapStyle` Literal in `schemas/map_bundle.py` and with
 * the cartograph worker's MapTilerClient.
 */
export type MapBundleStyle = 'topo-v2' | 'outdoor-v2' | 'satellite';

export const MAP_BUNDLE_STYLE_OPTIONS: ReadonlyArray<{
    value: MapBundleStyle;
    label: string;
    description: string;
}> = [
    {
        value: 'topo-v2',
        label: 'Topographic',
        description: 'Terrain, contours, hillshade — best for tactical / cross-country use.',
    },
    {
        value: 'outdoor-v2',
        label: 'Outdoor',
        description: 'Hiking-style cartography with trails and points of interest emphasised.',
    },
    {
        value: 'satellite',
        label: 'Satellite',
        description: 'Aerial imagery without labels — useful for visual reconnaissance.',
    },
];

export interface MapBundle {
    id: string;
    event_id: string;
    faction_id: string | null;
    created_by: string;
    name: string;
    aoo_north: number;
    aoo_south: number;
    aoo_east: number;
    aoo_west: number;
    style: MapBundleStyle;
    status: MapBundleStatusType;
    size_bytes: number | null;
    error: string | null;
    generated_at: string | null;
    created_at: string;
}

export interface MapBundleStatusUpdate {
    bundle_id: string;
    status: MapBundleStatusType;
    size_bytes: number | null;
    error: string | null;
    generated_at: string | null;
}

export interface AoOBounds {
    aoo_north: number;
    aoo_south: number;
    aoo_east: number;
    aoo_west: number;
}

export interface CreateBundlePayload extends AoOBounds {
    name: string;
    faction_id: string | null;
    style: MapBundleStyle;
}

export interface MapBundleState {
    bundles: MapBundle[];
    isLoading: boolean;
    isCreating: boolean;
    deletingIds: string[];
}
